
import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { mapReceiptApiToUi } from "@/lib/mappers/receipts";
import { generateMockReceipts } from "@/data/payments-mock-data";
import type { Receipt } from "@/types/payments";
import type { ReceiptsApiResponse } from "@/types/api/receipts";

export interface GetReceiptsParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  paymentMethod?: string;
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  hasPromise?: boolean;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * Hook to fetch receipts list data
 * Handles both Demo and Live modes
 */
export function useReceiptsList(params?: GetReceiptsParams) {
  const { apiGet } = useApi();
  const mockReceipts = generateMockReceipts();

  return useModeAwareQuery<Receipt[], Error>(
    ["receipts", params],
    async ({ signal }) => {
      const response = await apiGet<ReceiptsApiResponse>("/api/v1/receipts", params, signal);
      return response.data.map(mapReceiptApiToUi);
    },
    mockReceipts,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Combined hook for receipts page data
 * Returns receipts list with loading and error states
 */
export function useReceiptsPageData(params?: GetReceiptsParams) {
  const receiptsQuery = useReceiptsList(params);

  return {
    receipts: receiptsQuery.data ?? [],
    isLoading: receiptsQuery.isLoading,
    error: receiptsQuery.error,
    refetch: () => {
      receiptsQuery.refetch();
    },
  };
}
